import { ApiError } from '@app/api/errors';
import { ccfRoutes } from '@app/ccf/ccf-routes';
import Page from '@app/components/layout/page/page';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import { MONTHS_OPTIONS } from '@app/utils/constants';
import {
  formatDayWithSuffix,
  getDayOptionsForMonth,
  getMonthOptions,
} from '@app/utils/helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetOrganizationReportingPeriod } from '@shared/api/hooks/reports/periods';
import Text from '@shared/components/content/text';
import SelectFormField from '@shared/components/form/select-form-field';
import GuideLayout from '@shared/components/guide-layout';
import GuideTemplate from '@shared/components/guide-template';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { useHistory } from '@shared/providers/history-provider';
import { addYears, subDays } from 'date-fns';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { coerce, object, z } from 'zod';

const schema = object({
  startMonth: coerce
    .number({
      required_error: 'Start month is required',
    })
    .min(1, 'Select a valid month')
    .max(12, 'Select a valid month'),
  startDay: coerce
    .number({
      required_error: 'Start day is required',
    })
    .min(1, 'Select a valid day')
    .max(31, 'Select a valid day'),
});

export function ReportingPeriodsPage() {
  const navigate = useNavigate();
  const history = useHistory();
  const { toast } = useToast();
  const [referrer] = useState<string | undefined>(history.previousLocation);

  const {
    mutateAsync: setReportingPeriod,
    isLoading: creatingReportingPeriod,
  } = useSetOrganizationReportingPeriod();

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      startMonth: '1',
      startDay: '1',
    },
  });
  const { watch, setValue, getValues } = form;
  const selectedMonth = watch('startMonth');
  const selectedDay = watch('startDay');

  const monthOptions = getMonthOptions();
  const dayOptions = selectedMonth ? getDayOptionsForMonth(selectedMonth) : [];

  const handleOnClose = useMemo(() => {
    if (referrer?.startsWith('/purchased-goods')) {
      return referrer.startsWith('/purchased-goods/food-and-drink')
        ? generatePath(purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK)
        : generatePath(purchasedGoodsRoutes.PURCHASED_PACKAGING);
    }

    if (referrer?.startsWith('/sold-goods/')) {
      return referrer.startsWith('/sold-goods/food-and-drink')
        ? generatePath(soldGoodsRoutes.SOLD_FOOD_AND_DRINK)
        : generatePath(soldGoodsRoutes.SOLD_PACKAGING);
    }

    if (referrer?.startsWith('/company-reports/')) {
      return generatePath(ccfRoutes.COMPANY_REPORTS);
    }

    if (referrer?.startsWith('/sales-reports/')) {
      return generatePath(soldGoodsRoutes.SALES_REPORTS);
    }

    if (referrer?.startsWith('/purchase-reports')) {
      return generatePath(purchasedGoodsRoutes.PURCHASE_REPORTS);
    }

    return '/';
  }, [referrer]);

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const { startMonth, startDay } = data;
    try {
      await setReportingPeriod({
        data: {
          reportingYearStartDay: startDay,
          reportingYearStartMonth: startMonth,
        },
      });

      toast({
        variant: 'success',
        description: 'Your reporting period has been created',
      });

      referrer && navigate(referrer);
    } catch (error) {
      let description =
        'Please try again or get in touch if the error persists';

      if (
        error instanceof ApiError &&
        error.type === 'company-report-overlap'
      ) {
        description = 'This reporting period overlaps with an existing report';
      }

      toast({
        title: 'Error creating your report',
        variant: 'destructive',
        description,
      });
    }
  };

  const endDate = useMemo(() => {
    return subDays(
      addYears(new Date(2024, selectedMonth - 1, selectedDay), 1),
      1
    );
  }, [selectedDay, selectedMonth]);

  return (
    <GuideLayout
      pageName="Reporting periods"
      onClosePath={handleOnClose}
      useReferrer={false}
    >
      <Page name="Reporting periods" title="Reporting Periods" page="subpage">
        <GuideTemplate
          title="Set reporting period"
          description={
            <Text>
              All reporting data in our platform is assigned to a specific
              year-long period. A single company report will include data for a
              single one of these reporting periods.
              <br />
              <br />
              By default these follow the calendar year, but you can configure
              it differently if you wish to align your carbon reporting with
              your financial year for example. You can do so by setting an
              alternative start date for each year below. Once set, this cannot
              be updated.
            </Text>
          }
          primaryActionText="Set period"
          primaryActionProps={{
            type: 'submit',
            onClick: form.handleSubmit(onSubmit),
            'data-testid': 'set-period-button',
          }}
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="min-w-[300px] space-y-2 text-left"
            >
              <fieldset
                className="flex gap-4 "
                disabled={creatingReportingPeriod}
              >
                <SelectFormField
                  name="startMonth"
                  label="Start month"
                  options={monthOptions}
                  handleSelect={() => {
                    selectedDay > 28 && setValue('startDay', null);
                  }}
                  testId="start-month-select"
                />
                <SelectFormField
                  name="startDay"
                  label="Start day"
                  options={dayOptions}
                  disabled={!selectedMonth}
                  testId="start-day-select"
                />
              </fieldset>
              {selectedMonth && selectedDay && (
                <Text variant="subtle">
                  Your reporting years will run from{' '}
                  <span className="font-medium">
                    {
                      MONTHS_OPTIONS[
                        getValues(
                          'startMonth'
                        ).toString() as keyof typeof MONTHS_OPTIONS
                      ].shortName
                    }{' '}
                    {formatDayWithSuffix(selectedDay)}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {
                      MONTHS_OPTIONS[
                        (
                          endDate.getMonth() + 1
                        ).toString() as keyof typeof MONTHS_OPTIONS
                      ].shortName
                    }{' '}
                    {formatDayWithSuffix(endDate.getDate())}
                  </span>
                  .
                </Text>
              )}
            </form>
          </Form>
        </GuideTemplate>
      </Page>
    </GuideLayout>
  );
}
